import moment from 'moment-timezone';
import { PriceEditorConfig } from '@/domain/pricing/services/price-editor';
import { SalesRepConfig } from '@domain/configuration/types';
import { SalesRepInfoCostMap } from '@/domain/pricing/types';
import { SalesRepOverrideExpirationSource } from '@/types';
import { SalesRepOverrideMode, SalesRepPortionType } from '@/enums';
import type { SalesPortalOrderEntryTable_OrderEntryItemFieldsFragment } from '@/types';

/**
 * Transforms a sales portal order entry item and sales rep config into
 * a PriceEditorConfig for the domain PriceEditor class.
 *
 * @param item - Order entry item fragment containing variant pack info and price edit info
 * @param salesRepConfig - Supplier's sales rep config (override mode, floor/ceiling, PPP)
 * @returns A PriceEditorConfig ready for use with our PriceEditor class
 *
 * @example
 * ```typescript
 * const config = transformOrderEntryItemToPriceEditorConfig(
 *   item,
 *   salesRepConfig,
 * );
 * const editor = new PriceEditor(config);
 * ```
 */
export function transformOrderEntryItemToPriceEditorConfig(
  item: SalesPortalOrderEntryTable_OrderEntryItemFieldsFragment,
  salesRepConfig: SalesRepConfig,
): PriceEditorConfig {
  const priceEditInfo = item.variant_pack_info?.price_edit_info;
  const vpSettings = priceEditInfo?.variant_pack_settings;

  // Build costMap from cost_info.details array
  const costMap: SalesRepInfoCostMap = {};
  priceEditInfo?.cost_info?.details?.forEach((detail) => {
    costMap[detail.type] = detail.cost_in_micros;
  });

  const overrideMode =
    salesRepConfig.override_mode ?? SalesRepOverrideMode.DISABLED;

  const grossProfitDisplayConfig =
    salesRepConfig.display_options?.configs?.find(
      (config) => config.type === 'gross_profit',
    ) ?? null;

  return {
    costMap,
    currencyCode: item.variant_pack_info?.currency ?? null,
    // Only manually set expiration dates need to be displayed
    // Time/tz is truncated off as we only care about the date
    currentExpirationDate:
      !!salesRepConfig.can_set_manual_override_expiration &&
      priceEditInfo?.expiration_source ===
        SalesRepOverrideExpirationSource.SalesRepTemporary &&
      priceEditInfo.expires_at
        ? moment.parseZone(priceEditInfo.expires_at).format('YYYY-MM-DD')
        : null,
    currentGrossProfitOverridePercent:
      priceEditInfo?.gross_profit_override_in_percentage ?? null,
    currentPriceOverrideInMicros:
      priceEditInfo?.price_override_in_micros ?? null,
    customerCostType: salesRepConfig.customer_cost_type ?? null,
    defaultUnitPriceInMicros:
      item.variant_pack_info?.default_unit_price_in_micros ?? null,
    desiredPortionType:
      salesRepConfig.desired_portion_type ?? SalesRepPortionType.UNIT,
    disableOverrideEditing: !!(
      salesRepConfig.disable_override_editing ||
      vpSettings?.disable_override_editing
    ),
    disableSalesRepInfo: vpSettings?.disable_sales_rep_info ?? false,
    grossProfitDisplayConfig,
    onHandQuantity: null,
    overrideMode,
    peakPriceProtection:
      vpSettings?.enable_peak_price_protection ??
      salesRepConfig.peak_price_protection ??
      false,
    priceCeilingInMicros: priceEditInfo?.ceiling_price_in_micros ?? null,
    priceFloorEnabled: salesRepConfig.price_floor_enabled ?? false,
    // Must be per-unit (not pack) so GP calculations compare matching units
    priceInMicros: item.variant_pack_info?.unit_price_in_micros ?? null,
    salesRepGrossProfitEnabled:
      overrideMode === SalesRepOverrideMode.GROSS_PROFIT,
    salesRepPriceFloorMode: salesRepConfig.price_floor_mode ?? null,
    salesRepPricingEnabled: overrideMode === SalesRepOverrideMode.PRICE,
    unitCount: item.variant_pack_info?.unit_count ?? undefined,
  };
}
